import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  updateProfile,
  type User,
} from 'firebase/auth'
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore'
import { auth, db } from './firebase'
import { COLLECTIONS, type Role, type UserDoc } from '@shared/model'

interface AuthState {
  /** Firebase-användaren, eller null när ingen är inloggad. */
  user: User | null
  /** Vårt eget användardokument med roll, sparsaldo och notiser. */
  profile: UserDoc | null
  /** Rollen kommer från custom claims – klienten kan inte sätta den själv. */
  role: Role
  teamId: string | null
  /** True tills vi vet om någon är inloggad. Undviker att inloggningssidan blinkar förbi. */
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, displayName?: string) => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  /** Hämtar nya claims, t.ex. efter att en admin ändrat användarens roll. */
  refreshRole: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

/** Skriver lastActiveAt högst en gång per dygn – GDPR-gallringen tittar på det. */
const ACTIVITY_KEY = 'lira-last-activity-ping'

async function pingActivity(uid: string) {
  try {
    const today = new Date().toISOString().slice(0, 10)
    if (localStorage.getItem(ACTIVITY_KEY) === today) return
    localStorage.setItem(ACTIVITY_KEY, today)
    await updateDoc(doc(db, COLLECTIONS.users, uid), { lastActiveAt: serverTimestamp() })
  } catch {
    // Offline eller privat läge – helt ofarligt att hoppa över.
  }
}

/** Skapar användardokumentet om det saknas (t.ex. konto skapat via inbjudan). */
async function ensureUserDoc(user: User) {
  const ref = doc(db, COLLECTIONS.users, user.uid)
  const snapshot = await getDoc(ref)
  if (snapshot.exists()) return

  await setDoc(ref, {
    uid: user.uid,
    email: user.email ?? '',
    role: 'buyer',
    displayName: user.displayName ?? null,
    createdAt: serverTimestamp(),
    lastActiveAt: serverTimestamp(),
    savingsByBooklet: {},
    dismissedNotices: [],
  })
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserDoc | null>(null)
  const [role, setRole] = useState<Role>('buyer')
  const [teamId, setTeamId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Håller reda på dokumentlyssnaren så den städas bort vid utloggning.
  const unsubscribeProfile = useRef<(() => void) | null>(null)
  // Rollen som den senast lästes ur token, tillgänglig inuti lyssnaren utan
  // att den behöver sättas upp på nytt vid varje rollbyte.
  const roleRef = useRef<Role>('buyer')

  const readClaims = useCallback(async (current: User, force = false) => {
    const token = await current.getIdTokenResult(force)
    const claimed = (token.claims.role as Role) ?? 'buyer'
    roleRef.current = claimed
    setRole(claimed)
    setTeamId((token.claims.teamId as string) ?? null)
  }, [])

  useEffect(() => {
    return onAuthStateChanged(auth, async (current) => {
      unsubscribeProfile.current?.()
      unsubscribeProfile.current = null

      if (!current) {
        setUser(null)
        setProfile(null)
        roleRef.current = 'buyer'
        setRole('buyer')
        setTeamId(null)
        setLoading(false)
        return
      }

      setUser(current)

      try {
        await readClaims(current)
        await ensureUserDoc(current)
      } catch {
        // Offline: vi kör vidare på cachad data i stället för att låsa appen.
      }

      unsubscribeProfile.current = onSnapshot(
        doc(db, COLLECTIONS.users, current.uid),
        (snapshot) => {
          const next = snapshot.exists()
            ? ({ ...snapshot.data(), uid: current.uid } as UserDoc)
            : null
          setProfile(next)
          setLoading(false)

          // Rollen ligger i en signerad token som annars förnyas först efter
          // en timme. Dokumentet uppdateras samtidigt som claimen, så när de
          // två går isär vet vi att någon precis ändrat behörigheten – då
          // hämtar vi en ny token direkt i stället för att be personen logga
          // ut och in.
          if (next && next.role !== roleRef.current) {
            void readClaims(current, true)
          }
        },
        () => setLoading(false),
      )

      void pingActivity(current.uid)
    })
  }, [readClaims])

  useEffect(() => () => unsubscribeProfile.current?.(), [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      profile,
      role,
      teamId,
      loading,

      async signIn(email, password) {
        await signInWithEmailAndPassword(auth, email.trim(), password)
      },

      async signUp(email, password, displayName) {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password)
        if (displayName?.trim()) {
          await updateProfile(credential.user, { displayName: displayName.trim() })
        }
        await ensureUserDoc(credential.user)
      },

      async signOut() {
        await fbSignOut(auth)
      },

      async resetPassword(email) {
        /*
         * Med en adress att fortsätta till får Firebases sida en knapp
         * tillbaka hit när lösenordet är bytt. Utan den slutade det på en
         * främmande sida som bara sa "Du kan nu logga in" – utan att säga var.
         *
         * Adressen måste stå bland de godkända domänerna i Firebase, annars
         * vägrar Firebase skicka mejlet över huvud taget. Därför finns en
         * reserv: hellre ett mejl utan knapp än inget mejl alls, till exempel
         * från en förhandsversion på en adress som inte lagts till.
         */
        const continueTo = { url: `${window.location.origin}/logga-in` }
        try {
          await sendPasswordResetEmail(auth, email.trim(), continueTo)
        } catch (error) {
          if ((error as { code?: string }).code !== 'auth/unauthorized-continue-uri') throw error
          await sendPasswordResetEmail(auth, email.trim())
        }
      },

      async refreshRole() {
        if (auth.currentUser) await readClaims(auth.currentUser, true)
      },
    }),
    [user, profile, role, teamId, loading, readClaims],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth måste användas inuti <AuthProvider>')
  return context
}
